import { createUseStyles } from "react-jss";

import { GameState } from "actors/game.actor";

const styles = createUseStyles<any, {
    game: GameState,
    dayBrightness: (game: GameState) => number,
    dayColor: (game: GameState, red: number, green: number, blue: number) => string,
    wild: boolean,
}>({
    world: {
        pointerEvents: "all",
        position: "absolute",
        left: "0vw",
        right: "0vw",
        top: "0vh",
        bottom: "0vh",
        transform: "scale(1)",
    },
    layer: {
        pointerEvents: "none",
        position: "absolute",
        width: "100%",
        height: "100%",
        backgroundRepeatY: "no-repeat",
        backgroundRepeatX: "repeat",
        backgroundSize: "auto 100%",
        backgroundPosition: "bottom",
    },
    bob: {
        animation: "$bob-animation",
        animationDuration: 10000,
        animationIterationCount: "infinite",
        animationTimingFunction:  "ease-in-out",
        animationDirection:  "alternate",
        animationFillMode: "both",
    },
    "@keyframes bob-animation": {
        from: {
            backgroundPositionY: 0,
        },
        to: {
            backgroundPositionY: "-10vh",
        },
    },

    night: {
        backgroundSize: "auto",
        backgroundRepeat: "repeat",
        backgroundImage: "url(assets/regions/reefs/night.png)",
    },
    day: {
        backgroundSize: "auto",
        backgroundRepeat: "repeat",
        backgroundImage: "url(assets/regions/reefs/day.png)",
        filter: (props) => `
            opacity(${props.dayBrightness(props.game) * 0.75})
        `,
    },

    scene: {
        filter: (props) => `
            brightness(${0.5 + (props.dayBrightness(props.game) * 0.5)})
            contrast(${1.5 - (props.dayBrightness(props.game) * 0.5)})
        `,
    },
    actors: {
        filter: (props) => `
            brightness(${0.75 + (props.dayBrightness(props.game) * 0.25)})
            contrast(${1.5 - (props.dayBrightness(props.game) * 0.5)})
        `,
    },

    mountains: {
        bottom: 0,
        height: "70vh",
        backgroundImage: (props) => `url(assets/regions/reefs/corals${props.wild ? "_wild" : ""}.png)`,
    },
    hills: {
        bottom: 0,
        height: "50vh",
        backgroundImage: (props) => `url(assets/regions/reefs/seabed${props.wild ? "_wild" : ""}.png)`,
    },
    rocks: {
        bottom: 0,
        height: "25vh",
        zIndex: 200,
        backgroundImage: (props) => `url(assets/regions/reefs/rocks${props.wild ? "_wild" : ""}.png)`,
    },
    water: {
        top: 0,
        height: "100vh",
        zIndex: 200,
        backgroundImage: "url(assets/regions/reefs/water.png)",
        backgroundRepeat: "repeat",
        backgroundPositionX: (props) => (props.game.ticks * 0.25) % (1280 * 100),
        filter: (props) => `
            opacity(0.4)
        `,
    },
});


export default function Reefs(props: {
    game: GameState,
    actorComponents: JSX.Element[],
})
{
    // Day Colors:
    const dayBrightness = (game: GameState) => {
        let brightness: number = 1;
        switch (game.phase) {

            // Dawn:
            case 0:
                brightness = game.phaseProgress;
                break;

            // Day:
            case 1:
                brightness = 1;
                break;

            // Dusk:
            case 2:
                brightness = 1 - game.phaseProgress;
                break;

            // Night:
            default:
                brightness = 0;
        }
        return brightness;
    };

    const dayColor = (game: GameState, red: number, green: number, blue: number, forceAlpha: boolean = false) => {
        let alpha: number = forceAlpha ? 1 : dayBrightness(game);
        return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    };

    // Component:
    const classes = styles({
        game: props.game,
        dayBrightness,
        dayColor,
        wild: !props.game.homeZone,
    });
    return (
        <div className={`${classes.layer}`}>
            <div className={`${classes.layer} ${classes.bob} ${classes.night}`} />
            <div className={`${classes.layer} ${classes.bob} ${classes.day}`} />
            <div className={`${classes.layer} ${classes.scene}`}>
                <div className={`${classes.layer} ${classes.mountains}`} />
                <div className={`${classes.layer} ${classes.hills}`} />
            </div>
            <div className={`${classes.layer} ${classes.actors}`}>
                {props.actorComponents}
            </div>
            <div className={`${classes.layer} ${classes.scene}`}>
                <div className={`${classes.layer} ${classes.rocks}`} />
                <div className={`${classes.layer} ${classes.water}`} />
            </div>
        </div>
    );
}